'use client';
import clsx from 'clsx';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { trackAnalyticsEvent } from '@/app/lib/analytics';

import { Button } from '../../button';
import { TermsAndConditionsModal } from '../../terms-and-conditions-modal';
import { Text } from '../../text';
import { CheckboxVerticalVariant, SingularCheckbox } from '../checkbox';

import type { TermsAndConditionsFieldProps } from './index.types';
import type { FieldValues } from 'react-hook-form';

const TermsAndConditionsField = <T extends FieldValues>({
  analyticsEventName,
  checkboxLabel,
  checkboxName,
  children,
  ctaText,
  defaultCheckboxValue,
  hasHeadingBackground = true,
  modalHeading,
  onReadValue,
  readConditionsName,
  readRequired = true,
}: TermsAndConditionsFieldProps<T>) => {
  const { watch } = useFormContext<T>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const hasReadTerms = watch(readConditionsName);

  return (
    <div className="flex flex-col gap-d rounded-rad-a bg-white p-d">
      <div
        className={clsx('flex items-center justify-between', {
          'rounded-rad-a bg-gray-50 p-c': hasHeadingBackground,
          'border-b border-b-gray-200 pb-b': !hasHeadingBackground,
        })}
      >
        <Text
          className="text-left leading-none"
          variant="h3"
          as="p"
        >
          Terms & Conditions
        </Text>
      </div>

      <Button
        variant="secondary"
        onClick={() => {
          if (analyticsEventName) {
            trackAnalyticsEvent(analyticsEventName.view);
          }
          setIsModalOpen(true);
        }}
      >
        {ctaText}
      </Button>

      <SingularCheckbox<T>
        name={checkboxName}
        disabled={!hasReadTerms && readRequired}
        verticalVariant={CheckboxVerticalVariant.START}
        label={checkboxLabel}
        defaultValue={defaultCheckboxValue}
        analyticsEventName={analyticsEventName?.accepted}
      />
      <TermsAndConditionsModal<T>
        readConditionsName={readConditionsName}
        onReadValue={onReadValue}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        analyticsEventName={analyticsEventName?.return}
        heading={modalHeading}
      >
        {children}
      </TermsAndConditionsModal>
    </div>
  );
};

export default TermsAndConditionsField;
